// Shared display metadata for Sonic (labels, chips). Chips use Pivony's colours only (BrandTag
// tones): purple for done or going well, pink for waiting or wrong, neutral for the rest.

export const STATUS_COLORS = {
  pending_approval: "pink",
  queued: "neutral",
  sent: "neutral",
  delivered: "purple",
  replied: "purple",
  failed: "pink",
  in_progress: "purple",
  completed: "purpleSolid",
  scheduled: "pink",
};

export const STATUS_LABELS = {
  pending_approval: "Pending approval",
  queued: "Queued",
  sent: "Sent",
  delivered: "Delivered",
  replied: "Replied",
  failed: "Failed",
  in_progress: "In progress",
  completed: "Completed",
  scheduled: "Scheduled",
};

export const RULE_STATUS_COLORS = {
  active: "purple",
  paused: "pink",
  draft: "neutral",
};

export const APPROVAL_MODE_COLORS = {
  automatic: "purple",
  manual: "pink",
  hybrid: "neutral",
};

export const APPROVAL_MODE_DESCRIPTION_KEYS = {
  automatic: "sonic.approval_desc_automatic",
  manual: "sonic.approval_desc_manual",
  hybrid: "sonic.approval_desc_hybrid",
};

export const TRIGGER_TYPES = [
  {
    key: "insight",
    label: "Insight-triggered",
    description:
      "Fire when Pivony detects a signal (NPS drop, sentiment spike, VIP complaint).",
  },
  {
    key: "process",
    label: "Process-triggered",
    description:
      "Fire on external system events (order.shipped, delivery.failed, renewal due).",
  },
  {
    key: "voice",
    label: "Voice-triggered",
    description: "Start an AI voice conversation from a detected signal.",
    comingSoon: true,
  },
];

export const CHANNELS = [
  {
    key: "whatsapp",
    label: "WhatsApp",
    description: "Reach the customer with a WhatsApp Business message.",
  },
  {
    key: "voice_call",
    label: "AI Voice Call",
    description: "Place an outbound AI voice call.",
  },
  {
    key: "sms",
    label: "SMS",
    description: "Send a text message to the customer's phone.",
  },
  {
    key: "email",
    label: "Email",
    description: "Send the customer a transactional email.",
  },
  {
    key: "auto_select",
    label: "Auto-select",
    description: "Let Sonic pick the best channel per customer & context.",
  },
];

export const INSIGHT_SIGNALS = [
  { value: "nps_score", labelKey: "sonic.signal_nps_score" },
  { value: "sentiment_spike", labelKey: "sonic.signal_sentiment_spike" },
  { value: "vip_complaint", labelKey: "sonic.signal_vip_complaint" },
];

export const DATA_SOURCES = [
  { value: "reviews", label: "Reviews" },
  { value: "nps", label: "NPS" },
  { value: "tickets", label: "Support tickets" },
  { value: "surveys", label: "Surveys" },
  { value: "social", label: "Social" },
];

export const CHANNEL_ICON = {
  whatsapp: "whatsapp",
  voice_call: "voice",
  sms: "sms",
  email: "email",
};

// Tone/persona presets for the "Test your Voice Agent" sandbox.
export const PERSONA_OPTIONS = [
  { value: "friendly_empathetic", labelKey: "sonic.persona_friendly_empathetic" },
  { value: "professional_concise", labelKey: "sonic.persona_professional_concise" },
  { value: "upbeat_sales", labelKey: "sonic.persona_upbeat_sales" },
  { value: "calm_support", labelKey: "sonic.persona_calm_support" },
];

// Mirrors the backend cap (per org, per rolling hour) for display only.
export const TEST_CALL_MAX_PER_HOUR = 5;

/** BCP-47 language tags supported by Sonic voice + WhatsApp templates. */
export const SUPPORTED_LANGUAGES = [
  { value: "tr-TR", label: "Türkçe" },
  { value: "en-US", label: "English" },
  { value: "de-DE", label: "Deutsch" },
  { value: "ar-SA", label: "العربية" },
];

// Language a stored rule/template/agent without one is in (the backend default).
export const DEFAULT_LANGUAGE = "en-US";

// Languages a phone call can be held in (ElevenLabs Eleven v3): the person called may
// not speak the org's language.
export const CALL_LANGUAGES = [
  { value: "tr-TR", label: "Türkçe" },
  { value: "en-US", label: "English" },
  { value: "de-DE", label: "Deutsch" },
  { value: "ru-RU", label: "Русский" },
  { value: "ar-SA", label: "العربية" },
  { value: "fr-FR", label: "Français" },
  { value: "es-ES", label: "Español" },
  { value: "it-IT", label: "Italiano" },
  { value: "nl-NL", label: "Nederlands" },
];

export const getLanguageLabel = (code) => {
  const match = SUPPORTED_LANGUAGES.find((l) => l.value === code);
  return match ? match.label : code || DEFAULT_LANGUAGE;
};

const FALLBACK_TIMEZONES = [
  "UTC",
  "Europe/Istanbul",
  "Europe/Berlin",
  "Europe/London",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Dubai",
  "Asia/Riyadh",
];

export const suggestBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) {
    return "UTC";
  }
};

export const getIanaTimezoneOptions = () => {
  try {
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone").map((tz) => ({
        value: tz,
        label: tz.replace(/_/g, " "),
      }));
    }
  } catch (e) {
    // ignore
  }
  return FALLBACK_TIMEZONES.map((tz) => ({
    value: tz,
    label: tz.replace(/_/g, " "),
  }));
};

/** Map API channel value to template UI segmented control. */
export const templateChannelUi = (channel) =>
  channel === "voice_call" ? "voice" : channel || "whatsapp";

/** Map template UI channel to API payload. */
export const templateChannelApi = (channel) =>
  channel === "voice" ? "voice_call" : channel || "whatsapp";

export const CALL_RETENTION_OPTIONS = [
  { value: 30, labelKey: "sonic.retention_30" },
  { value: 90, labelKey: "sonic.retention_90" },
  { value: 180, labelKey: "sonic.retention_180" },
  { value: null, labelKey: "sonic.retention_unlimited" },
];

export const MAX_ATTEMPT_PERIOD_OPTIONS = [
  { value: "day", labelKey: "sonic.max_attempts_period_day" },
  { value: "week", labelKey: "sonic.max_attempts_period_week" },
];

export const SONIC_CRM_PROVIDERS = [
  { key: "salesforce", labelKey: "sonic.integration_salesforce" },
  { key: "hubspot", labelKey: "sonic.integration_hubspot" },
  { key: "zendesk", labelKey: "sonic.integration_zendesk" },
];

export const DEFAULT_CALL_LIMITS = {
  calling_hours_enabled: true,
  calling_hours_start: "09:00",
  calling_hours_end: "20:00",
  exclude_weekends: true,
  exclude_public_holidays: false,
  max_attempts_per_contact: 3,
  max_attempts_period: "day",
};

export const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};
